import type { Ref } from 'vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { isCancel } from 'axios';

import { useLoaderStore } from '@/stores/useLoaderStore';
import { fetchGetJobDraft } from '@/helpers/fetchers';
import * as routeNames from '@/constants/routeNames';

export interface DraftsJobBookingViewDataComposable {
  isFetchingAllData: Ref<boolean>;
  allData: Ref<
    | {
        drafts?: openapi.JobDraftListContentOpenapi;
      }
    | null
    | undefined
  >;
}

export default function useDraftsJobBookingViewData(
  queryCurPage: Readonly<Ref<number>>,
  queryPageSize: Readonly<Ref<number>>,
): DraftsJobBookingViewDataComposable {
  const route = useRoute();
  const loaderStore = useLoaderStore();

  let controller: AbortController | undefined;
  const allDataFetchCounter = ref(0);
  const isFetchingAllData = computed(() => !!allDataFetchCounter.value);
  const allData = ref<{
    drafts?: openapi.JobDraftListContentOpenapi;
  } | null>();

  watch(
    [queryCurPage, queryPageSize],
    async () => {
      if (route.name !== routeNames.DRAFT_JOB_BOOKING) return;
      const curPage = queryCurPage.value;
      const pageSize = queryPageSize.value;
      if (!curPage || !pageSize) {
        if (controller) controller.abort();
        allData.value = null;
        return;
      }
      if (controller) controller.abort();
      controller = new AbortController();
      loaderStore.increaseGlobalLoader();
      allDataFetchCounter.value++;
      try {
        const data = await fetchGetJobDraft(
          { page: curPage, page_size: pageSize },
          { signal: controller.signal },
        );
        allData.value = { drafts: data };
      } catch (err: unknown) {
        if (!isCancel(err)) allData.value = null;
      } finally {
        allDataFetchCounter.value--;
        loaderStore.decreaseGlobalLoader();
      }
    },
    { immediate: true, deep: true },
  );

  return {
    isFetchingAllData,
    allData,
  };
}
